import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaBriefcase } from 'react-icons/fa';
import './AgencyExperience.css';
import Swal from 'sweetalert2';
import BaseUrl from '../../../../ApiServices/BaseUrl';
import { Container, Card, Row, Col, Button, Dropdown, Form } from 'react-bootstrap';
import AgencyNavbar from '../../../../Pages/Navbar/AgencyNavbar';

const AgencyExperience = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, current_country, job_position } = location.state || {};

  // Log userId to console
  useEffect(() => {
    console.log('Received userId:', userId);
    console.log('Received userName:', userName);
    console.log('Received current_country:', current_country);
    console.log('Received job_position:', job_position);
  }, [userId, current_country, job_position]);

  const medicalSpecialties = [
    "Allergist/Immunologist", "Anesthesiologist", "Cardiologist", "Dermatologist",
    "Endocrinologist", "Gastroenterologist", "General Surgeon", "Infectious Disease Specialist",
    "Nephrologist", "Neurologist", "Oncologist", "Ophthalmologist", "Orthopedic Surgeon",
    "Otolaryngologist (ENT)", "Pathologist", "Pediatrician", "Psychiatrist", "Pulmonologist",
    "Radiologist", "Rheumatologist", "Urologist"
  ];

  const surgicalSpecialties = [
    "Cardiothoracic Surgeon", "Colorectal Surgeon", "General Surgeon", "Neurosurgeon",
    "Oral and Maxillofacial Surgeon", "Orthopedic Surgeon", "Plastic Surgeon",
    "Trauma Surgeon", "Vascular Surgeon"
  ];

  const medicalJobPositions = [
    "Emergency Medicine Physician", "Family Medicine Physician", "General Practitioner",
    "Intensivist", "Medical Geneticist", "Occupational Medicine Physician",
    "Pain Management Specialist", "Palliative Care Physician",
    "Preventive Medicine Physician", "Public Health Physician"
  ];

  const researchJobPositions = [
    "Professor of Medicine", "Research Scientist", "Clinical Research Coordinator",
    "Medical Educator", "Dean of Medical School"
  ];

  const leadershipJobPositions = [
    "Hospital Administrator", "Medical Director", "Department Chair",
    "Chief Medical Officer (CMO)", "Healthcare Consultant"
  ];

  const otherLanguages = [
    "DELF", "DALF", "TCF", "DELE", "TestDaF", "Goethe-Zertifikat", "DSD", "CILS",
    "PLIDA", "HSK", "BTC", "JLPT", "NAT-TEST", "ALPT", "ACTFL", "TORFL", "TRKI",
    "CELPE-Bras", "DEPLE", "TOPIK", "KLAT"
  ];

  const certificateOptions = ["Yes", "No"];
  const validPassportOptions = ["Yes", "Applied", "Not Yet"];
  const lastbuttonsoption = ["Back", "Save", "Next"];

  // State for form fields
  const [postInternship, setPostInternship] = useState(0);
  const [postGraduation, setPostGraduation] = useState(0);
  const [totalExperience, setTotalExperience] = useState(0);
  const [linkedInProfile, setLinkedInProfile] = useState('');
  const [currentHospital, setCurrentHospital] = useState('');
  const [currentHospitalCountry, setCurrentHospitalCountry] = useState('');
  const [currentWorkStartDate, setCurrentWorkStartDate] = useState('');
  const [currentWorkEndDate, setCurrentWorkEndDate] = useState('...On going');
  const [previousHospitals, setPreviousHospitals] = useState([{
    previous_hospital_name: '',
    hospital_country_name: '',
    work_start_date: '',
    work_end_date: ''
  }]);
  const [selectedLeadershipJob, setSelectedLeadershipJob] = useState('');
  const [workedCountries, setWorkedCountries] = useState([]);
  const [certificateOption, setCertificateOption] = useState('');
  const [validPassport, setValidPassport] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to filter options for dropdown
  const filterOptions = (options, searchTerm) => {
    if (!searchTerm) return options;
    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Custom Dropdown Component (same as in JobPreference)
  const CustomDropdown = ({
    title,
    field,
    options,
    selectedValue,
    onSelect,
    required = false,
    menuClassName = '',
    disabled = false
  }) => {
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    const filteredOptions = filterOptions(options, localSearchTerm);

    return (
      <div>
        <Dropdown
          className="mb-3"
          show={isOpen}
          onToggle={(isOpen) => setIsOpen(isOpen)}
          disabled={disabled}
        >
          <Dropdown.Toggle
            variant="light"
            className="language-select w-100 d-flex justify-content-between align-items-center"
            id={`dropdown-${field}`}
            disabled={disabled}
          >
            {selectedValue || `${title}${required ? ' *' : ''}`}
          </Dropdown.Toggle>
          <Dropdown.Menu className={`w-100 ${menuClassName}`}>
            <div className="px-2 py-1">
              <Form.Control
                type="text"
                placeholder={`Search ${title.toLowerCase()}...`}
                value={localSearchTerm}
                onChange={(e) => setLocalSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="mb-2"
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Dropdown.Item
                    key={option}
                    active={selectedValue === option}
                    onClick={() => {
                      onSelect(field, option);
                      setLocalSearchTerm('');
                      setIsOpen(false);
                    }}
                  >
                    {option}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No matches found</Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  // Fetch existing data
  useEffect(() => {
    const fetchExperienceData = async () => {
      if (!userId) return;

      try {
        setIsLoading(true);
        const response = await fetch(`${BaseUrl}/api/job-seeker/doctor/${userId}`);

        if (response.ok) {
          const data = await response.json();
          console.log('Fetched experience data:', data);

          // Set experience fields
          if (data.dtr_internship_clinical_experience !== null && data.dtr_internship_clinical_experience !== undefined) {
            setPostInternship(data.dtr_internship_clinical_experience);
          }

          if (data.dtr_graduation_clinical_experience !== null && data.dtr_graduation_clinical_experience !== undefined) {
            setPostGraduation(data.dtr_graduation_clinical_experience);
          }

          if (data.dtr_years_of_experience_doctor !== null && data.dtr_years_of_experience_doctor !== undefined) {
            setTotalExperience(data.dtr_years_of_experience_doctor);
          }

          if (data.dtr_linkedin_profile && data.dtr_linkedin_profile !== "null") {
            setLinkedInProfile(data.dtr_linkedin_profile);
          }

          if (data.dtr_currently_working_hospital_name && data.dtr_currently_working_hospital_name !== "null") {
            setCurrentHospital(data.dtr_currently_working_hospital_name);
          }

          if (data.dtr_hospital_country && data.dtr_hospital_country !== "null") {
            setCurrentHospitalCountry(data.dtr_hospital_country);
          }

          if (data.dtr_work_start_date && data.dtr_work_start_date !== "null") {
            const startDate = new Date(data.dtr_work_start_date);
            if (!isNaN(startDate.getTime())) {
              setCurrentWorkStartDate(startDate.toISOString().split('T')[0]);
            } else {
              setCurrentWorkStartDate('');
            }
          }

          if (data.dtr_work_end_date && data.dtr_work_end_date !== "null" && data.dtr_work_end_date !== "...On going") {
            const endDate = new Date(data.dtr_work_end_date);
            if (!isNaN(endDate.getTime())) {
              setCurrentWorkEndDate(endDate.toISOString().split('T')[0]);
            } else {
              setCurrentWorkEndDate('');
            }
          } else {
            setCurrentWorkEndDate('...On going');
          }

          if (data.dtr_previous_hospital_details && data.dtr_previous_hospital_details !== "null") {
            try {
              const parsedHospitals = JSON.parse(data.dtr_previous_hospital_details);
              if (Array.isArray(parsedHospitals) && parsedHospitals.length > 0) {
                const formattedHospitals = parsedHospitals.map(hospital => {
                  let startDate = '';
                  let endDate = '';

                  if (hospital.work_start_date) {
                    const dateObj = new Date(hospital.work_start_date);
                    if (!isNaN(dateObj.getTime())) {
                      startDate = dateObj.toISOString().split('T')[0];
                    }
                  }

                  if (hospital.work_end_date && hospital.work_end_date !== "...On going") {
                    const dateObj = new Date(hospital.work_end_date);
                    if (!isNaN(dateObj.getTime())) {
                      endDate = dateObj.toISOString().split('T')[0];
                    }
                  } else if (hospital.work_end_date === "...On going") {
                    endDate = "...On going";
                  }

                  return {
                    previous_hospital_name: hospital.previous_hospital_name || '',
                    hospital_country_name: hospital.hospital_country_name || '',
                    work_start_date: startDate,
                    work_end_date: endDate
                  };
                });
                setPreviousHospitals(formattedHospitals);
              }
            } catch (e) {
              console.error('Error parsing previous hospitals:', e);
            }
          }

          if (data.dtr_administrative_leadership_job_position && data.dtr_administrative_leadership_job_position !== "null") {
            setSelectedLeadershipJob(data.dtr_administrative_leadership_job_position);
          }

          if (data.dtr_before_worked_country && data.dtr_before_worked_country !== "null") {
            try {
              const parsedCountries = JSON.parse(data.dtr_before_worked_country);
              if (Array.isArray(parsedCountries)) {
                setWorkedCountries(parsedCountries);
              }
            } catch (e) {
              console.error('Error parsing worked countries:', e);
            }
          }

          if (data.dtr_certificate_options && data.dtr_certificate_options !== "null") {
            try {
              const parsedCertificates = JSON.parse(data.dtr_certificate_options);
              if (Array.isArray(parsedCertificates) && parsedCertificates.length > 0) {
                setCertificateOption(parsedCertificates[0]);
              }
            } catch (e) {
              console.error('Error parsing certificate options:', e);
            }
          }

          if (data.dtr_valid_passport !== null && data.dtr_valid_passport !== undefined) {
            if (data.dtr_valid_passport === true || data.dtr_valid_passport === 1) {
              setValidPassport("Yes");
            } else if (data.dtr_valid_passport === false || data.dtr_valid_passport === 0) {
              setValidPassport("Not Yet");
            }
          }
        } else if (response.status !== 404) {
          throw new Error('Failed to fetch experience data');
        }
      } catch (err) {
        console.error('Error fetching experience data:', err);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to load your experience information. Please try again.',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#d33',
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchExperienceData();
    }
  }, [userId]);

  // Show success alert using SweetAlert2
  const showSuccessAlert = () => {
    Swal.fire({
      title: 'Success!',
      text: 'Experience information saved successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#3085d6',
    });
  };

  // Handle save data to API
  const handleSave = async () => {
    if (!userId) {
      Swal.fire({
        title: 'Error!',
        text: 'User ID is missing. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    }

    setIsSaving(true);

    try {
      const formData = {
        user_id: userId,
        dtr_user_id: userId,
        dtr_internship_clinical_experience: postInternship,
        dtr_graduation_clinical_experience: postGraduation,
        dtr_years_of_experience_doctor: totalExperience,
        dtr_linkedin_profile: linkedInProfile,
        dtr_currently_working_hospital_name: currentHospital,
        dtr_hospital_country: currentHospitalCountry,
        dtr_work_start_date: currentWorkStartDate,
        dtr_work_end_date: '...On going',
        dtr_previous_hospital_details: previousHospitals,
        dtr_administrative_leadership_job_position: selectedLeadershipJob,
        dtr_before_worked_country: workedCountries,
        dtr_certificate_options: certificateOption ? [certificateOption] : [],
        dtr_valid_passport: validPassport === "Yes"
      };

      console.log('Saving experience data:', formData);

      const response = await fetch(`${BaseUrl}/api/job-seeker/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        showSuccessAlert();
        return true;
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        throw new Error(errorData.message || 'Failed to save experience information');
      }
    } catch (error) {
      console.error('Error saving experience information:', error);
      Swal.fire({
        title: 'Error!',
        text: error.message || 'Error saving experience information. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#d33',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  // Handle next step
  const handleNext = async () => {
    const success = await handleSave();
    if (success) {
      navigate('/a-qualifications', {
        state: { userId: userId }
      });
    }
  };

  // Handle back step
  const handleBack = () => {
    navigate(-1);
  };

  // Handle navigation button clicks
  const handleNavigation = (buttonName) => {
    if (buttonName === "Next") {
      handleNext();
    } else if (buttonName === "Back") {
      handleBack();
    } else if (buttonName === "Save") {
      handleSave();
    }
  };

  // Add more previous hospital fields
  const addMoreHospital = () => {
    setPreviousHospitals([...previousHospitals, {
      previous_hospital_name: '',
      hospital_country_name: '',
      work_start_date: '',
      work_end_date: ''
    }]);
  };

  // Update previous hospital field
  const updatePreviousHospital = (index, field, value) => {
    const updatedHospitals = [...previousHospitals];
    updatedHospitals[index][field] = value;
    setPreviousHospitals(updatedHospitals);
  };

  // Toggle country selection
  const toggleCountry = (country) => {
    if (workedCountries.includes(country)) {
      setWorkedCountries(workedCountries.filter(c => c !== country));
    } else {
      setWorkedCountries([...workedCountries, country]);
    }
  };

  // Remove selected worked country
  const removeWorkedCountry = (country) => {
    setWorkedCountries(workedCountries.filter(c => c !== country));
  };

  if (isLoading) {
    return (
      <>
        <AgencyNavbar />
        <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <AgencyNavbar />
      <div className="experience-wrapper" style={{ marginTop: "84px", width: '96%', marginLeft: '2%' }}>
        <Container className="mt-4 px-4 pb-5">
          <Card className="shadow border-0 professional-info-card">
            <Card.Header className="professional-info-header">
              <h2 className="m-0 text-white text-center">
                <FaBriefcase className="me-2" />
                Previous Experience
              </h2>
            </Card.Header>
            <Card.Body className="professional-info-body">

              {/* Post Internship */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Post Internship Clinical Experience
                </h4>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="experience-circle">{postInternship}</div>
                  <input
                    type="range"
                    className="experience-range flex-grow-1 mx-3"
                    min="0"
                    max="40"
                    value={postInternship}
                    onChange={(e) => setPostInternship(parseInt(e.target.value))}
                  />
                  <div className="experience-circle">{postInternship}</div>
                </div>
              </div>

              {/* Post Graduation */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Post Graduation Clinical Experience
                </h4>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="experience-circle">{postGraduation}</div>
                  <input
                    type="range"
                    className="experience-range flex-grow-1 mx-3"
                    min="0"
                    max="40"
                    value={postGraduation}
                    onChange={(e) => setPostGraduation(parseInt(e.target.value))}
                  />
                  <div className="experience-circle">{postGraduation}</div>
                </div>
              </div>

              {/* Total Experience */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Total Years of Experience as a Doctor <span className="text-danger">*</span>
                </h4>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="experience-circle">{totalExperience}</div>
                  <input
                    type="range"
                    className="experience-range flex-grow-1 mx-3"
                    min="0"
                    max="40"
                    value={totalExperience}
                    onChange={(e) => setTotalExperience(parseInt(e.target.value))}
                  />
                  <div className="experience-circle">{totalExperience}</div>
                </div>
              </div>

              {/* Current Hospital */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Which hospital do you currently work at?
                </h4>
                <Row className="g-3 mb-3">
                  <Col xs={12} lg={6}>
                    <input
                      type="text"
                      className="language-btn experience-input form-control"
                      placeholder="Type your Current hospital name"
                      value={currentHospital}
                      onChange={(e) => setCurrentHospital(e.target.value)}
                    />
                  </Col>
                  <Col xs={12} lg={6} style={{ marginTop: '-4px' }}>
                    <CustomDropdown
                      title="Select country"
                      field="current_hospital_country"
                      options={[
                        "UAE", "Dubai", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
                        "Brunei", "Singapore", "Malaysia", "Hong Kong", "Japan", "South Korea",
                        "Taiwan", "Germany", "Netherlands", "United Kingdom", "Italy", "Spain",
                        "France", "Poland", "Portugal", "Romania", "Hungary", "Canada",
                        "United States", "Australia", "New Zealand", "India", "Bangladesh",
                        "Nepal", "Sri Lanka", "Pakistan", "Philippines", "Indonesia", "Myanmar",
                        "Vietnam", "Cambodia", "Thailand", "Ethiopia", "Kenya", "Uganda",
                        "Tanzania", "Rwanda", "Ghana", "Nigeria", "Sierra Leone", "Burundi",
                        "Cameroon"
                      ]}
                      selectedValue={currentHospitalCountry}
                      onSelect={(field, value) => setCurrentHospitalCountry(value)}
                      required={false}
                    />
                  </Col>
                </Row>
                <Row className="g-3 mb-4">
                  <Col xs={12} lg={6}>
                    <input
                      type="date"
                      className="language-btn experience-input form-control"
                      value={currentWorkStartDate}
                      onChange={(e) => setCurrentWorkStartDate(e.target.value)}
                      style={{ textTransform: 'uppercase' }}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <div className="d-flex align-items-center h-100">
                      <span className="language-btn experience-input form-control bg-light">
                        ...On going
                      </span>
                      <input
                        type="hidden"
                        value="...On going"
                        name="currentWorkEndDate"
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Previous Hospital */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Previous Hospital Name
                </h4>
                {previousHospitals.map((hospital, index) => (
                  <div key={index} className="mb-4">
                    <Row className="g-3 mb-3">
                      <Col xs={12} lg={6}>
                        <input
                          type="text"
                          className="language-btn experience-input form-control"
                          placeholder="Type your previous hospital name"
                          value={hospital.previous_hospital_name}
                          onChange={(e) => updatePreviousHospital(index, 'previous_hospital_name', e.target.value)}
                        />
                      </Col>
                      <Col xs={12} lg={6} style={{ marginTop: '-4px' }}>
                        <CustomDropdown
                          title="Select country"
                          field={`previous_hospital_country_${index}`}
                          options={[
                            "UAE", "Dubai", "Saudi Arabia", "Qatar", "Kuwait", "Bahrain", "Oman",
                            "Brunei", "Singapore", "Malaysia", "Hong Kong", "Japan", "South Korea",
                            "Taiwan", "Germany", "Netherlands", "United Kingdom", "Italy", "Spain",
                            "France", "Poland", "Portugal", "Romania", "Hungary", "Canada",
                            "United States", "Australia", "New Zealand", "India", "Bangladesh",
                            "Nepal", "Sri Lanka", "Pakistan", "Philippines", "Indonesia", "Myanmar",
                            "Vietnam", "Cambodia", "Thailand", "Ethiopia", "Kenya", "Uganda",
                            "Tanzania", "Rwanda", "Ghana", "Nigeria", "Sierra Leone", "Burundi",
                            "Cameroon"
                          ]}
                          selectedValue={hospital.hospital_country_name}
                          onSelect={(field, value) => updatePreviousHospital(index, 'hospital_country_name', value)}
                          required={false}
                        />
                      </Col>
                    </Row>
                    <Row className="g-3 mb-4">
                      <Col xs={12} lg={6}>
                        <input
                          type="date"
                          className="language-btn experience-input form-control"
                          value={hospital.work_start_date}
                          onChange={(e) => updatePreviousHospital(index, 'work_start_date', e.target.value)}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </Col>
                      <Col xs={12} lg={6}>
                        <input
                          type="date"
                          className="language-btn experience-input form-control"
                          value={hospital.work_end_date}
                          onChange={(e) => updatePreviousHospital(index, 'work_end_date', e.target.value)}
                          style={{ textTransform: 'uppercase' }}
                        />
                      </Col>
                    </Row>
                  </div>
                ))}

                <div className="text-end">
                  <button className="experience-btn" style={{ width: '100%' }} onClick={addMoreHospital}>
                    <span className="me-2">+</span> Add More
                  </button>
                </div>
              </div>

              {/* Leadership Position */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Select Administrative And Leadership Job Position
                </h4>
                <CustomDropdown
                  title="Select / type"
                  field="leadership_job"
                  options={leadershipJobPositions}
                  selectedValue={selectedLeadershipJob}
                  onSelect={(field, value) => setSelectedLeadershipJob(value)}
                  required={false}
                />
              </div>

              {/* Certificate Options */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Certificate Options <span className="text-danger">*</span>
                </h4>

                <Row className="g-3 mb-4">
                  {certificateOptions.map((btn, i) => (
                    <Col xs={6} lg={6} key={i}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${certificateOption === btn ? 'selected-button' : ''}`}
                        onClick={() => setCertificateOption(btn)}
                        style={{ backgroundColor: certificateOption === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Valid Passport */}
              <div className="mb-5">
                <h4 className="mb-3 fw-semibold text-dark d-flex align-items-center fields-headingfont">
                  Do You have a Valid Passport? <span className="text-danger">*</span>
                </h4>

                <Row className="g-3 mb-4">
                  {validPassportOptions.map((btn, i) => (
                    <Col xs={4} lg={4} key={i}>
                      <Button
                        className={`language-btn jobtype-btn w-100 ${validPassport === btn ? 'selected-button' : ''}`}
                        onClick={() => setValidPassport(btn)}
                        style={{ backgroundColor: validPassport === btn ? '#0d6efd' : '#aec9f3' }}
                      >
                        {btn}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>

              {/* Navigation Buttons */}
              <div className="additional-section mb-4" style={{ marginTop: '20px' }}>
                <div className="d-flex justify-content-between gap-2" style={{ width: "100%" }}>
                  {lastbuttonsoption.map((btn, i) => (
                    <button
                      key={i}
                      className="nav-btn"
                      style={{ flex: 1 }}
                      onClick={() => handleNavigation(btn)}
                      disabled={isSaving}
                    >
                      {isSaving && (btn === "Save" || btn === "Next") ? 'Saving...' : btn}
                    </button>
                  ))}
                </div>
              </div>

            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  );
};

export default AgencyExperience;